import React, { useEffect, useMemo, useState } from 'react';
import {
	FlatList,
	KeyboardAvoidingView,
	Modal,
	Platform,
	Pressable,
	StyleSheet,
	Text,
	TextInput,
	TouchableOpacity,
	View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Colors from 'src/core/utils/Colors';
import Dimens from 'src/core/utils/Dimens';
import Fonts from 'src/core/utils/Fonts';
import Translations from 'src/core/utils/Translations';
import FieldError from 'src/presentation/components/atoms/FieldError';
import { filterOptions } from 'src/core/utils/filterOptions';
import { limitReached } from 'src/core/utils/formChoiceLimits';
import type { ApiFormAnswer, ApiFormQuestion, ApiFormQuestionOption } from 'src/core/models/api/shared/ApiFormQuestion';

interface SearchableSelectFieldProps {
	name: string;
	data: ApiFormQuestion;
	answers?: Record<string, ApiFormAnswer>;
	onSwitch: (value: string) => void;
	error?: string;
}

function answerFor(answers: SearchableSelectFieldProps['answers'], name: string): string {
	const existing = answers?.[name];
	return typeof existing === 'string' ? existing : '';
}

function SearchableSelectField({ name, data, answers, onSwitch, error }: SearchableSelectFieldProps) {
	const insets = useSafeAreaInsets();
	const [chosenValue, setChosenValue] = useState(answerFor(answers, name));
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState('');

	useEffect(() => {
		setChosenValue(answerFor(answers, name));
	}, [answers, name]);

	const options = useMemo(() => data.options ?? [], [data.options]);
	const visible = useMemo(() => filterOptions(options, query), [options, query]);

	const close = () => {
		setOpen(false);
		setQuery('');
	};

	const choose = (option: ApiFormQuestionOption) => {
		if (limitReached(option) && option.name !== chosenValue) {
			return;
		}
		setChosenValue(option.name);
		onSwitch(option.name);
		close();
	};

	const renderOption = ({ item }: { item: ApiFormQuestionOption }) => {
		const selected = item.name === chosenValue;
		const disabled = limitReached(item) && !selected;
		return (
			<TouchableOpacity
				accessibilityRole="button"
				accessibilityState={{ selected, disabled }}
				style={[styles.option, selected ? styles.optionSelected : null]}
				onPress={() => choose(item)}
			>
				<Text style={[styles.optionText, selected ? styles.optionTextSelected : null, disabled ? styles.disabled : null]}>
					{item.name}
				</Text>
				{selected ? <Ionicons color={Colors.secondary} name="checkmark" size={22} /> : null}
			</TouchableOpacity>
		);
	};

	return (
		<View>
			<Text style={styles.question}>
				{data.label}
				{data.required ? '*' : null}
			</Text>
			{error ? <FieldError message={error} /> : null}
			<Pressable accessibilityRole="button" style={styles.trigger} onPress={() => setOpen(true)}>
				<Text numberOfLines={1} style={[styles.triggerText, chosenValue ? null : styles.placeholder]}>
					{chosenValue || Translations.placeholderChooseOption}
				</Text>
				<Ionicons color={Colors.greyTextDark} name="chevron-down" size={20} />
			</Pressable>

			<Modal animationType="slide" visible={open} onRequestClose={close}>
				<KeyboardAvoidingView
					behavior={Platform.OS === 'ios' ? 'padding' : undefined}
					style={[styles.modal, { paddingTop: insets.top, paddingBottom: insets.bottom }]}
				>
					<View style={styles.header}>
						<Text numberOfLines={2} style={styles.headerTitle}>
							{data.label}
						</Text>
						<TouchableOpacity accessibilityRole="button" onPress={close}>
							<Text style={styles.close}>{Translations.buttonClose}</Text>
						</TouchableOpacity>
					</View>
					<View style={styles.searchWrapper}>
						<Ionicons color={Colors.greyTextDark} name="search" size={18} />
						<TextInput
							autoCapitalize="none"
							autoCorrect={false}
							autoFocus={true}
							clearButtonMode="while-editing"
							placeholder={Translations.placeholderSearch}
							placeholderTextColor={Colors.lightGrey}
							returnKeyType="search"
							selectionColor={Colors.secondary}
							style={styles.search}
							value={query}
							onChangeText={setQuery}
						/>
					</View>
					<FlatList
						ListEmptyComponent={<Text style={styles.empty}>{Translations.textNoResultsFound}</Text>}
						data={visible}
						initialNumToRender={20}
						keyExtractor={(item, index) => `${index}-${item.name}`}
						keyboardDismissMode="on-drag"
						keyboardShouldPersistTaps="handled"
						renderItem={renderOption}
						windowSize={11}
					/>
				</KeyboardAvoidingView>
			</Modal>
		</View>
	);
}

export default SearchableSelectField;

const styles = StyleSheet.create({
	question: {
		fontFamily: Platform.OS === 'android' ? Fonts.montserratRegular : Fonts.montserratBold,
		fontSize: Dimens.fontSize16,
		color: Colors.greyTextDark,
		lineHeight: 20,
		paddingBottom: Dimens.padding8,
	},
	trigger: {
		flexDirection: 'row',
		alignItems: 'center',
		paddingHorizontal: Dimens.padding8,
		paddingVertical: Dimens.padding12,
		borderBottomWidth: 1,
		borderBottomColor: Colors.greyBorder,
	},
	triggerText: {
		flex: 1,
		fontFamily: Fonts.montserratRegular,
		fontSize: Dimens.fontSize16,
		color: Colors.greyTextDark,
	},
	placeholder: {
		color: Colors.lightGrey,
	},
	modal: {
		flex: 1,
		backgroundColor: Colors.white,
	},
	header: {
		flexDirection: 'row',
		alignItems: 'center',
		paddingHorizontal: Dimens.padding16,
		paddingVertical: Dimens.padding12,
	},
	headerTitle: {
		flex: 1,
		fontFamily: Fonts.montserratBold,
		fontSize: Dimens.fontSize16,
		color: Colors.greyTextDark,
	},
	close: {
		fontFamily: Fonts.montserratRegular,
		fontSize: Dimens.fontSize16,
		color: Colors.secondary,
		paddingLeft: Dimens.padding12,
	},
	searchWrapper: {
		flexDirection: 'row',
		alignItems: 'center',
		marginHorizontal: Dimens.margin16,
		marginBottom: Dimens.margin8,
		paddingHorizontal: Dimens.padding12,
		borderRadius: 8,
		backgroundColor: Colors.greyBgAlt,
	},
	search: {
		flex: 1,
		paddingVertical: Dimens.padding12,
		paddingLeft: Dimens.padding8,
		fontFamily: Fonts.montserratRegular,
		fontSize: Dimens.fontSize16,
		color: Colors.greyTextDark,
	},
	option: {
		flexDirection: 'row',
		alignItems: 'center',
		paddingHorizontal: Dimens.padding16,
		paddingVertical: Dimens.padding12,
		borderBottomWidth: 1,
		borderBottomColor: Colors.greyBorder,
	},
	optionSelected: {
		backgroundColor: Colors.greyBg,
	},
	optionText: {
		flex: 1,
		fontFamily: Fonts.montserratRegular,
		fontSize: Dimens.fontSize16,
		color: Colors.greyTextDark,
	},
	optionTextSelected: {
		fontFamily: Fonts.montserratBold,
		color: Colors.secondary,
	},
	disabled: {
		color: Colors.greyText,
	},
	empty: {
		padding: Dimens.padding16,
		fontFamily: Fonts.montserratRegular,
		fontSize: Dimens.fontSize16,
		color: Colors.greyTextDark,
	},
});
